import type { NodeType, NodeSubtype } from '../types';

interface AllocationDescription {
  tagline: string;
  explanation: string;
}

const SUBTYPE_DESCRIPTIONS: Record<string, AllocationDescription> = {
  CHECKING: {
    tagline: 'Covers your essentials',
    explanation: 'Rent, utilities, groceries - the basics are always taken care of.',
  },
  SAVINGS: {
    tagline: 'Builds your safety net',
    explanation: 'So an unexpected expense never throws you off track.',
  },
  CREDIT_CARD: {
    tagline: 'Keeps debt in check',
    explanation: 'Paying down your balance to stay ahead.',
  },
  MORTGAGE: {
    tagline: 'Keeps your home secure',
    explanation: 'Consistent payments that protect your biggest asset.',
  },
  PERSONAL_LOAN: {
    tagline: 'Pays down your loan',
    explanation: 'Chipping away at your balance so you can be debt-free sooner.',
  },
  HOME_LOAN: {
    tagline: 'Keeps your home secure',
    explanation: 'Consistent payments that protect your biggest asset.',
  },
  STUDENT_LOAN: {
    tagline: 'Invests in your education',
    explanation: 'Staying on top of payments so your degree works for you.',
  },
  AUTO_LOAN: {
    tagline: 'Keeps you on the road',
    explanation: 'Staying current so your car stays yours.',
  },
  BUSINESS_LOAN: {
    tagline: 'Fuels your business growth',
    explanation: 'Paying down debt so more profit stays in your pocket.',
  },
  BROKERAGE: {
    tagline: 'Grows your wealth over time',
    explanation: 'Through the power of compounding in diversified investments.',
  },
  IRA: {
    tagline: 'Earmarked for retirement',
    explanation: 'Your future self will thank you for starting now.',
  },
  PENSION: {
    tagline: 'Earmarked for retirement',
    explanation: 'Your future self will thank you for starting now.',
  },
  EDUCATION_SAVINGS: {
    tagline: 'Saves for education',
    explanation: 'Building a fund so tuition day is stress-free.',
  },
};

const TYPE_FALLBACKS: Partial<Record<NodeType, AllocationDescription>> = {
  LIABILITY_ACCOUNT: {
    tagline: 'Keeps debt in check',
    explanation: 'Staying on top of payments to keep your finances healthy.',
  },
  INVESTMENT_ACCOUNT: {
    tagline: 'Grows your wealth over time',
    explanation: 'Putting your money to work for your future.',
  },
  DEPOSITORY_ACCOUNT: {
    tagline: 'Keeps your cash organized',
    explanation: 'The right amount in the right place at the right time.',
  },
};

const REMAINDER_DESCRIPTION: AllocationDescription = {
  tagline: 'Yours to enjoy guilt-free',
  explanation: 'Dining out, hobbies, entertainment - the things that make life fun.',
};

// Name-based matching for POD / untyped nodes
const POD_NAME_PATTERNS: Array<{ pattern: RegExp; desc: AllocationDescription }> = [
  {
    pattern: /tax/i,
    desc: { tagline: 'Tax Reserve', explanation: 'Never be caught off guard when tax season hits.' },
  },
  {
    pattern: /operat/i,
    desc: { tagline: 'Operating Expenses', explanation: 'Keeps the lights on and the business running smoothly.' },
  },
  {
    pattern: /profit/i,
    desc: { tagline: 'Profit Reserve', explanation: 'Your reward for running a smart business - locked away before it disappears.' },
  },
  {
    pattern: /owner.?pay|pay.*self/i,
    desc: { tagline: 'Owner Pay', explanation: 'Your personal paycheck, automatically carved out every cycle.' },
  },
  {
    pattern: /bill/i,
    desc: { tagline: 'Bills', explanation: 'Your non-negotiables, covered before you even think about them.' },
  },
  {
    pattern: /fun|spend|entertainment/i,
    desc: { tagline: 'Fun Money', explanation: 'Because you work hard and deserve to enjoy some of it.' },
  },
  {
    pattern: /grocer|food/i,
    desc: { tagline: 'Groceries & Food', explanation: 'Keeping the fridge full without the mental math.' },
  },
  {
    pattern: /travel|vacation/i,
    desc: { tagline: 'Travel Fund', explanation: 'Every dollar here brings your next trip closer.' },
  },
  {
    pattern: /gift|giving|charit|donat/i,
    desc: { tagline: 'Giving', explanation: 'Generosity on autopilot - birthdays and holidays, handled.' },
  },
  {
    pattern: /emergency/i,
    desc: { tagline: 'Emergency Fund', explanation: 'So an unexpected expense never throws you off track.' },
  },
  {
    pattern: /invest/i,
    desc: { tagline: 'Investments', explanation: 'Putting your money to work before you have a chance to spend it.' },
  },
  {
    pattern: /retire/i,
    desc: { tagline: 'Retirement', explanation: 'Your future self will thank you for starting now.' },
  },
];

function matchPodByName(name: string): AllocationDescription | null {
  for (const { pattern, desc } of POD_NAME_PATTERNS) {
    if (pattern.test(name)) return desc;
  }
  return null;
}

export function getAllocationDescription(
  nodeType: NodeType,
  nodeSubtype: NodeSubtype | undefined,
  nodeName: string,
  isRemainder: boolean,
): AllocationDescription {
  if (isRemainder) return REMAINDER_DESCRIPTION;

  if (nodeSubtype && SUBTYPE_DESCRIPTIONS[nodeSubtype]) {
    return SUBTYPE_DESCRIPTIONS[nodeSubtype];
  }

  if (TYPE_FALLBACKS[nodeType]) {
    return TYPE_FALLBACKS[nodeType];
  }

  // Try name-based matching for PODs and unknown types
  const podMatch = matchPodByName(nodeName);
  if (podMatch) return podMatch;

  return {
    tagline: nodeName,
    explanation: 'Set aside automatically so it\'s ready when you need it.',
  };
}
